import { z } from 'zod';
import { listOf } from '../fields/list';
import { optionalPhone } from '../fields/phone';
import { optionalText } from '../fields/text';
import { defineForm } from '../types';
import { ENTRA_USER_ATTRIBUTES } from '../catalogues/entra-user';

/**
 * A new client's first form: who they are, and the people NICS should set up.
 *
 * The people are a list — rows of the same few boxes — which the submitter can type
 * in or bring from a CSV template. Names use the Entra catalogue's rules so they are
 * ready to become accounts; the email is the person's *current* address, since their
 * NICS-managed one does not exist yet, and no username is asked for: NICS assigns it.
 *
 * Offered to the operator today. When invited clients can sign in (the CIAM source),
 * that source names this form explicitly; until then nobody else sees it.
 */

const SECTIONS = {
  company: 'Primary Contact Info',
  people: 'Contacts',
  more: 'Anything else',
} as const;

const personShape = {
  givenName: ENTRA_USER_ATTRIBUTES.givenName.schema,
  surname: ENTRA_USER_ATTRIBUTES.surname.schema,
  email: z.email('Enter a valid email address'),
  directPhone: optionalPhone(),
  canApprovePasswordResets: z.boolean(),
  notes: optionalText(250),
};

export const onboardingPeople = listOf(
  personShape,
  [
    { name: 'givenName', label: 'First name', widget: 'text' },
    { name: 'surname', label: 'Last name', widget: 'text' },
    { name: 'email', label: 'Email', widget: 'email', placeholder: 'name@example.com' },
    { name: 'directPhone', label: 'Direct phone', widget: 'tel', placeholder: '(306) 555-0123' },
    { name: 'canApprovePasswordResets', label: 'Can approve password resets', widget: 'checkbox' },
    { name: 'notes', label: 'Notes', widget: 'text' },
  ],
  // A person is identified by their email: uploading a corrected sheet updates rows.
  { min: 1, max: 200, rowLabel: 'person', key: ['email'] },
);

export const clientOnboardingSchema = z.object({
  companyName: z.string().trim().min(1, 'Required').max(128, 'Keep this under 128 characters'),
  primaryContactName: z
    .string()
    .trim()
    .min(1, 'Required')
    .max(128, 'Keep this under 128 characters'),
  primaryContactEmail: z.email('Enter a valid email address'),
  primaryContactPhone: optionalPhone(),

  people: onboardingPeople.schema,

  notes: optionalText(2000),
});

export type ClientOnboardingInput = z.input<typeof clientOnboardingSchema>;
export type ClientOnboarding = z.output<typeof clientOnboardingSchema>;

export const clientOnboarding = defineForm({
  id: 'client-onboarding',
  version: '1.0.0',
  // The operator files these on a new client's behalf until invited clients can sign
  // in themselves; that identity source will name this form explicitly.
  customers: ['1095', '0000'],
  title: 'Client onboarding',
  description: 'Add Contacts here or download and fill in a templated spreadsheet to add in bulk',
  schema: clientOnboardingSchema,
  caseTitle: (payload) => payload.companyName,
  fields: [
    {
      name: 'companyName',
      label: 'Company name',
      widget: 'text',
      section: SECTIONS.company,
    },
    {
      name: 'primaryContactName',
      label: 'Primary contact',
      widget: 'text',
      helpText: 'Who NICS should talk to about this onboarding.',
      section: SECTIONS.company,
    },
    {
      name: 'primaryContactEmail',
      label: 'Contact email',
      widget: 'email',
      section: SECTIONS.company,
    },
    {
      name: 'primaryContactPhone',
      label: 'Contact phone',
      widget: 'tel',
      placeholder: '(306) 555-0123',
      section: SECTIONS.company,
    },

    {
      name: 'people',
      label: 'People',
      widget: 'list',
      columns: onboardingPeople.columns,
      list: onboardingPeople.list,
      helpText:
        'One row per person. Tick "Can approve password resets" for anyone can reset other people\'s passwords.',
      section: SECTIONS.people,
    },

    {
      name: 'notes',
      label: 'Notes',
      widget: 'textarea',
      helpText: 'Optional. Anything else NICS should know for your onboarding',
      section: SECTIONS.more,
    },
  ],
});
